import React, { useState } from 'react';
import { Autocomplete, Box, Chip, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const normalize = (arr) => {
  const out = [];
  for (const raw of arr) {
    const t = String(raw).trim();
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
};

// Free-text chip input: typed text converts to a chip on Enter, Tab, comma,
// or arrow keys. Backspace on an empty input removes the last chip; blur
// commits any pending text.
// `options` supplies suggestions (e.g. existing tags) shown while typing —
// picking one adds it; anything else still becomes a new chip (freeSolo).
// `infoTip` renders an info icon with a hover tooltip next to the label.
// Layout props (sx, fullWidth, size) belong on the Autocomplete root so the
// component sizes like a plain TextField in flex rows.
const ChipInputField = ({
  label,
  value = [],
  onChange,
  options = [],
  infoTip,
  placeholder,
  helperText,
  sx,
  size,
  fullWidth = true,
  ...textFieldProps
}) => {
  const [inputValue, setInputValue] = useState('');

  const commitInput = () => {
    const t = inputValue.trim();
    if (!t) return;
    onChange(normalize([...value, t]));
    setInputValue('');
  };

  // Enter is handled natively by Autocomplete (freeSolo); the rest are ours.
  // Up/down are left to the listbox when suggestions are showing.
  const handleKeyDown = (e) => {
    const commitKeys = options.length
      ? [',', 'Tab', 'ArrowLeft', 'ArrowRight']
      : [',', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (commitKeys.includes(e.key) && inputValue.trim()) {
      e.preventDefault();
      commitInput();
    }
  };

  const labelNode = infoTip ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label}
      <Tooltip title={infoTip} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
      </Tooltip>
    </Box>
  ) : (
    label
  );

  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      options={options}
      filterSelectedOptions
      sx={sx}
      size={size}
      fullWidth={fullWidth}
      value={value}
      onChange={(e, newValue) => onChange(normalize(newValue))}
      inputValue={inputValue}
      onInputChange={(e, v) => setInputValue(v)}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} size="small" label={option} {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={labelNode}
          placeholder={value.length === 0 ? placeholder : undefined}
          helperText={helperText}
          onKeyDown={handleKeyDown}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default ChipInputField;
